import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Button, CircularProgress, Alert, Box, Dialog,
  DialogActions, DialogContent, DialogTitle, Stack, Chip, Divider, InputAdornment
} from '@mui/material';
import { Edit, LocationOn, TrendingUp, LocalShipping, Agriculture } from '@mui/icons-material';

const RateManagement = () => {
  const { t } = useTranslation();
  const [areaRates, setAreaRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [newLorryRate, setNewLorryRate] = useState('');
  const [newTractorRate, setNewTractorRate] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAreaRates = useCallback(async () => {
    try {
      setError('');
      const response = await axios.get('/admin/rates');
      setAreaRates(response.data);
    } catch (err) {
      setError(err.response?.data?.message || t('error') || 'Failed to fetch rates');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAreaRates();
  }, [fetchAreaRates]);

  const handleUpdateRate = async () => {
    if (!selectedZone) return;
    setSaving(true);
    try {
      await axios.put(`/admin/rates/${selectedZone._id}`, {
        lorryRate: parseFloat(newLorryRate) || 0,
        tractorRate: parseFloat(newTractorRate) || 0
      });
      await fetchAreaRates();
      setOpenDialog(false);
      setSelectedZone(null);
      setNewLorryRate('');
      setNewTractorRate('');
    } catch (err) {
      setError(err.response?.data?.message || t('error') || 'Failed to update rate');
    } finally {
      setSaving(false);
    }
  };

  const openUpdateDialog = (zone) => {
    setSelectedZone(zone);
    setNewLorryRate(zone.lorryRate.toString());
    setNewTractorRate(zone.tractorRate.toString());
    setOpenDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh', alignItems: 'center' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: '#f8fafc', minHeight: '90vh' }}>
      {/* Header section */}
      <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: 'white', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, right: 0, width: '200px', height: '100%', background: 'linear-gradient(45deg, transparent, #eff6ff)', opacity: 0.5 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
              {t('rateManagement') || 'Rate Management'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('ratesDescription') || 'Manage vehicle pricing rates by service zone'}
            </Typography>
          </Box>
          <Chip
            icon={<TrendingUp sx={{ color: '#059669 !important' }} />}
            label={`${areaRates.length} zone${areaRates.length !== 1 ? 's' : ''}`}
            sx={{ fontWeight: 700, px: 1, backgroundColor: '#ecfdf5', color: '#059669', borderRadius: 2 }}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {areaRates.length > 0 ? (
        <TableContainer component={Paper} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 2.5 }}>
                  {t('area') || 'Service Zone'}
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                  {t('location') || 'Location'}
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <LocalShipping sx={{ fontSize: 18, color: '#1976d2' }} />
                    <span>Lorry Rate</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Agriculture sx={{ fontSize: 18, color: '#2e7d32' }} />
                    <span>Tractor Rate</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#475569' }} align="right">
                  {t('action') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {areaRates.map((areaRate) => (
                <TableRow key={areaRate._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1, borderRadius: 2, backgroundColor: '#f1f5f9' }}>
                        <LocationOn sx={{ color: '#64748b' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>{areaRate.area}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {areaRate.city}{areaRate.city && areaRate.state ? ', ' : ''}{areaRate.state}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800, color: '#1976d2', fontSize: '1.05rem' }}>
                      ₹{(areaRate.lorryRate || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800, color: '#2e7d32', fontSize: '1.05rem' }}>
                      ₹{(areaRate.tractorRate || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={areaRate.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={areaRate.isActive ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openUpdateDialog(areaRate)}
                      startIcon={<Edit />}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      {t('updateRate') || 'Adjust Rate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 10, textAlign: 'center', borderRadius: 4, border: '1px dashed #cbd5e1', backgroundColor: 'transparent' }}>
          <Typography variant="h6" color="text.secondary">
            {t('noData') || 'No service zones found. Create zones in Zone Management first.'}
          </Typography>
        </Paper>
      )}

      {/* Update Rate Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !saving && setOpenDialog(false)}
        PaperProps={{ sx: { borderRadius: 4, p: 1, width: '440px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, mt: 1 }}>
          {t('adjustRegionalRate') || 'Adjust Zone Rates'}
        </DialogTitle>
        <DialogContent>
          {selectedZone && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>CURRENT ZONE</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedZone.area}</Typography>
              {(selectedZone.city || selectedZone.state) && (
                <Typography variant="caption" color="text.secondary">
                  {selectedZone.city}{selectedZone.city && selectedZone.state ? ', ' : ''}{selectedZone.state}
                </Typography>
              )}
            </Box>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocalShipping sx={{ fontSize: 18 }} /> Lorry (Truck) Rate
              </Typography>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={newLorryRate}
                onChange={(e) => setNewLorryRate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography component="span" sx={{ fontWeight: 700, color: '#1976d2' }}>₹</Typography>
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter lorry rate"
                inputProps={{ min: 0 }}
              />
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Agriculture sx={{ fontSize: 18 }} /> Tractor Rate
              </Typography>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={newTractorRate}
                onChange={(e) => setNewTractorRate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography component="span" sx={{ fontWeight: 700, color: '#2e7d32' }}>₹</Typography>
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter tractor rate"
                inputProps={{ min: 0 }}
              />
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            * These rates will also be reflected in Zone Management.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit" sx={{ fontWeight: 700 }} disabled={saving}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleUpdateRate}
            variant="contained"
            disabled={saving || !newLorryRate || !newTractorRate}
            sx={{ borderRadius: 2, px: 3, fontWeight: 700, backgroundColor: '#2563eb' }}
          >
            {saving ? 'Saving...' : (t('applyChange') || 'Apply Change')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RateManagement;